import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { useAuth } from '../contexts/AuthContext';
import UserPopup from '../components/Sidebar';
import { ArrowUpIcon, ChevronDownIcon, ClipboardIcon } from '../components/Icons';
import { copyToClipboard } from '../utils/clipboard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || `${window.location.protocol}//${window.location.hostname}:8000/api`;

const usePersistedState = (key, defaultValue) => {
  const [state, setState] = useState(() => {
    const saved = localStorage.getItem(key);
    return saved !== null ? JSON.parse(saved) : defaultValue;
  });

  useEffect(() => {
    localStorage.setItem(key, JSON.stringify(state));
  }, [key, state]);

  return [state, setState];
};

const Message = React.memo(({ role, content, isStreaming = false }) => {
  const [copied, setCopied] = useState(false);
  
  const renderedContent = useMemo(() => {
    if (role === 'user') return content;

    return (
      <ReactMarkdown
        components={{
          code({node, inline, className, children, ...props}) {
            if (!inline) {
              const codeText = String(children).replace(/\n$/, '');
              return (
                <div style={{position:'relative'}}>
                  <button className="copy-button" onClick={() => {
                    copyToClipboard(codeText); setCopied(true);
                    setTimeout(() => setCopied(false),2e3);
                  }} title="Copy code">{copied ? 'Copied!' : <ClipboardIcon/>}</button>
                  <SyntaxHighlighter style={oneLight} language={/language-(\w+)/.exec(className || '')?.[1]} PreTag="div" {...props}>
                    {codeText}
                  </SyntaxHighlighter>
                </div>
              );
            }
            return <code {...props}>{children}</code>;
          }
        }}
      >
        {content}
      </ReactMarkdown>
    );
  }, [content, role]);

  return (
    <div className={`message-container ${role}`}>
      {role === 'user' ? (
        <div className="message user-message">{renderedContent}</div>
      ) : (
        <div className="message assistant-message">
          <div className="message-content">
            {renderedContent}
            {isStreaming && <span className="cursor-blink">|</span>}
          </div>
        </div>
      )}
    </div>
  );
});

Message.displayName = 'Message';

const ModernInputBox = ({ 
  input, 
  setInput, 
  onSubmit, 
  isLoading, 
  disabled, 
  placeholder = "Enter a message...",
  className = "",
  selectedModel,
  onModelChange,
  availableModels
}) => {
  const textareaRef = useRef(null);
  const [modelPopupOpen, setModelPopupOpen] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const modelButtonRef = useRef(null);

  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 384) + 'px'; // max-height: 24rem (384px)
    }
  }, []);

  useEffect(() => {
    adjustTextareaHeight();
  }, [input, adjustTextareaHeight]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSubmit();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit();
  };

  const handleModelSelect = (model) => {
    onModelChange(model);
    setModelPopupOpen(false);
  };

  const toggleModelPopup = () => {
    if (!modelPopupOpen && modelButtonRef.current) {
      const rect = modelButtonRef.current.getBoundingClientRect();
      const popupHeight = 200; // Approximate popup height
      const spacing = 8;
      
      setPopupPosition({
        top: rect.top - popupHeight - spacing,
        left: rect.left,
      });
    }
    setModelPopupOpen(!modelPopupOpen);
  };

  // Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modelButtonRef.current && !modelButtonRef.current.contains(event.target)) {
        setModelPopupOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={`modern-input-container ${className}`}>
      <form onSubmit={handleSubmit} className="modern-input-form">
        <div className="modern-input-wrapper">
          <div className="input-content">
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={placeholder}
              disabled={disabled}
              className="modern-textarea"
              rows={1}
            />
          </div>
        </div>
        
        <div className="input-controls">
          <div className="left-controls">
            <div className="model-selector-container" ref={modelButtonRef}>
              <button 
                type="button" 
                className="model-selector-button"
                onClick={toggleModelPopup}
                title="Select AI Model"
              >
                <span className="model-name">{selectedModel}</span>
                <ChevronDownIcon size={14} className={`chevron ${modelPopupOpen ? 'open' : ''}`} />
              </button>
              
              {modelPopupOpen && (
                <div 
                  className="model-popup"
                  style={{
                    top: `${popupPosition.top}px`,
                    left: `${popupPosition.left}px`,
                  }}
                >
                  <div className="model-popup-content">
                    {availableModels.map(model => (
                      <button
                        key={model}
                        type="button"
                        className={`model-option ${model === selectedModel ? 'selected' : ''}`}
                        onClick={() => handleModelSelect(model)}
                      >
                        {model}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <div className="right-controls">
            <button 
              type="submit"
              disabled={!input.trim() || disabled}
              className="send-button-modern"
            >
              <ArrowUpIcon size={16} />
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

// Custom hook for smart scrolling behavior
const useSmartScroll = (messagesAreaRef, dependencies = []) => {
  const scrollTimeoutRef = useRef(null);
  const isUserScrollingRef = useRef(false);
  const lastScrollTopRef = useRef(0);

  // Check if user is near the bottom of the scroll area
  const isNearBottom = useCallback(() => {
    const element = messagesAreaRef.current;
    if (!element) return true;
    
    const { scrollTop, scrollHeight, clientHeight } = element;
    const threshold = 100; // pixels from bottom
    return scrollHeight - scrollTop - clientHeight < threshold;
  }, [messagesAreaRef]);

  // Smooth scroll to bottom with debouncing
  const scrollToBottom = useCallback((force = false) => {
    const element = messagesAreaRef.current;
    if (!element) return;

    // Only auto-scroll if user is near bottom or force is true
    if (!force && !isNearBottom()) return;

    // Clear any pending scroll
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    // Debounce scrolling to prevent excessive calls
    scrollTimeoutRef.current = setTimeout(() => {
      if (element && (force || isNearBottom())) {
        element.scrollTo({
          top: element.scrollHeight,
          behavior: 'smooth'
        });
      }
    }, 50); // 50ms debounce
  }, [messagesAreaRef, isNearBottom]);

  // Handle scroll events to detect user scrolling
  const handleScroll = useCallback(() => {
    const element = messagesAreaRef.current;
    if (!element) return;

    const currentScrollTop = element.scrollTop;
    
    // Detect if user is manually scrolling (not programmatic)
    if (Math.abs(currentScrollTop - lastScrollTopRef.current) > 5) {
      isUserScrollingRef.current = true;
      
      // Reset user scrolling flag after a delay
      setTimeout(() => {
        isUserScrollingRef.current = false;
      }, 1000);
    }
    
    lastScrollTopRef.current = currentScrollTop;
  }, [messagesAreaRef]);

  // Effect to handle scrolling based on dependencies
  useEffect(() => {
    const element = messagesAreaRef.current;
    if (!element) return;

    // Add scroll listener
    element.addEventListener('scroll', handleScroll, { passive: true });

    // Scroll to bottom when dependencies change
    scrollToBottom();

    return () => {
      element.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  return { scrollToBottom, isNearBottom };
};

const ChatInterface = ({ auth }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedModel, setSelectedModel] = useState('gpt-4.1');
  const [availableModels, setAvailableModels] = useState(['gpt-4', 'gpt-3.5']);
  const [messageCount, setMessageCount] = usePersistedState('ai_chat_count', 0);
  const [streamingMessage, setStreamingMessage] = useState('');

  const maxMessages = 50;
  
  const messagesAreaRef = useRef(null);
  const streamingContentRef = useRef('');
  const lastStreamUpdateRef = useRef(0);

  // Use smart scrolling hook
  const { scrollToBottom } = useSmartScroll(messagesAreaRef, [messages.length]);

  // Throttled streaming update to reduce UI thrashing
  const updateStreamingMessage = useCallback((content) => {
    const now = Date.now();
    
    // Store the latest content
    streamingContentRef.current = content;
    
    // Throttle updates to every 100ms for smooth streaming
    if (now - lastStreamUpdateRef.current < 100) return;
    
    lastStreamUpdateRef.current = now;
    setStreamingMessage(content);
    
    // Gentle scroll during streaming
    setTimeout(() => scrollToBottom(), 10);
  }, [scrollToBottom]);

  // Force scroll when new messages are added
  useEffect(() => {
    if (messages.length > 0) {
      setTimeout(() => scrollToBottom(true), 100);
    }
  }, [messages.length, scrollToBottom]);

  useEffect(() => {
    const fetchModels = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/models`);
        if (res.ok) {
          const data = await res.json();
          setAvailableModels(data.models);
        }
      } catch (e) {
        console.error('Failed to fetch models:', e);
      }
    };
    fetchModels();
  }, []);

  const clearChat = useCallback(() => {
    setMessages([]);
    setStreamingMessage('');
    setMessageCount(0);
    streamingContentRef.current = '';
  }, [setMessageCount]);

  // eslint-disable-next-line no-loop-func
  const sendMessage = useCallback(async () => {
    if (!input.trim() || isLoading || messageCount >= maxMessages) return;

    const userMessage = { role: 'user', content: input.trim() };
    const updatedMessages = [...messages, userMessage];
    
    setMessages(updatedMessages);
    setInput('');
    setIsLoading(true);
    setStreamingMessage('');
    streamingContentRef.current = '';
    setMessageCount(c => c + 1);

    let timeoutId = setTimeout(() => {
      setIsLoading(false);
      setStreamingMessage('');
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: '⚠️ Request timed out. Please try again.' 
      }]);
    }, 120000);

    try {
      const response = await fetch(`${BACKEND_URL}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.access_token}`
        },
        body: JSON.stringify({
          messages: updatedMessages.slice(-20), // Keep recent context
          model: selectedModel,
          stream: true
        }),
      });

      if (!response.ok) {
        clearTimeout(timeoutId);
        setIsLoading(false);
        setStreamingMessage('');
        
        if (response.status === 429) {
          setMessageCount(c => Math.max(0, c - 1));
          const errorData = await response.json().catch(() => ({}));
          const detail = errorData.detail || 'Too many requests';
          
          setMessages(prev => [...prev, { 
            role: 'assistant', 
            content: `⚠️ ${detail.includes('Too fast') ? 'Please wait between messages' : 'Rate limit exceeded. Please wait and try again.'}` 
          }]);
          return;
        }
        
        if (response.status === 401) {
          localStorage.removeItem('ai_auth');
          window.location.reload();
          return;
        }
        
        throw new Error(`Service error: ${response.status}`);
      }

      // Handle streaming response
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let accumulatedContent = '';
      let completed = false;
      
      const resetTimeout = () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = setTimeout(() => {
            if (!completed) {
              setMessages(prev => [...prev, { 
                role: 'assistant', 
                content: '⚠️ Request timed out. Please try again.' 
              }]);
              setIsLoading(false);
              setStreamingMessage('');
            }
          }, 120000);
        }
      };

      try {
        while (!completed) {
          const { value, done } = await reader.read();
          
          if (done) break;
          
          resetTimeout();
          
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (!line.trim() || !line.startsWith('data: ') || completed) continue;
            
            try {
              const jsonStr = line.slice(6).trim();
              if (!jsonStr) continue;
              
              const data = JSON.parse(jsonStr);
              
              if (data.type === 'content_delta' && data.content) {
                accumulatedContent += data.content;
                updateStreamingMessage(accumulatedContent);
              } else if (data.type === 'completion') {
                completed = true;
                // Use the latest content from ref to avoid race conditions
                const finalContent = streamingContentRef.current || accumulatedContent;
                setMessages(prev => [...prev, { role: 'assistant', content: finalContent }]);
                setStreamingMessage('');
                break;
              } else if (data.type === 'error') {
                completed = true;
                throw new Error(data.error);
              }
            } catch (parseError) {
              console.warn('Parse error:', parseError);
            }
          }
        }
        
        // Handle incomplete streams
        if (!completed && accumulatedContent) {
          const finalContent = streamingContentRef.current || accumulatedContent;
          setMessages(prev => [...prev, { role: 'assistant', content: finalContent }]);
        } else if (!completed) {
          setMessages(prev => [...prev, { 
            role: 'assistant', 
            content: '⚠️ Response was incomplete. Please try again.' 
          }]);
        }
        
      } catch (streamError) {
        console.error('Stream error:', streamError);
        if (!completed) {
          setMessages(prev => [...prev, { 
            role: 'assistant', 
            content: '⚠️ Stream error occurred. Please try again.' 
          }]);
        }
      } finally {
        try {
          reader.cancel();
        } catch (e) {
          // Ignore cancel errors
        }
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: '⚠️ Service temporarily unavailable. Please try again.' 
      }]);
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
      setIsLoading(false);
      setStreamingMessage('');
      streamingContentRef.current = '';
    }
  }, [input, isLoading, messages, messageCount, auth, selectedModel, maxMessages, setMessageCount, updateStreamingMessage]);

  const hasMessages = messages.length > 0 || streamingMessage;

  const handleLogout = () => {
    localStorage.removeItem('ai_auth');
    window.location.reload();
  };

  return (
    <div className="modern-chat-page">
      {/* Floating User Menu */}
      <div className="floating-user-menu">
        <UserPopup
          auth={auth}
          onLogout={handleLogout}
          onClearChat={clearChat}
        />
      </div>

      {/* Main Chat Area */}
      <div className="chat-content">
        {!hasMessages ? (
          /* Centered Input for Empty State */
          <div className="empty-state">
            <div className="welcome-message">
              <h3>Welcome to the AI Chat Interface</h3>
              <p>Start a conversation to test AI response capabilities</p>
            </div>
            <ModernInputBox
              input={input}
              setInput={setInput}
              onSubmit={sendMessage}
              isLoading={isLoading}
              disabled={isLoading || messageCount >= maxMessages}
              className="centered-input"
              selectedModel={selectedModel}
              onModelChange={setSelectedModel}
              availableModels={availableModels}
            />
          </div>
        ) : (
          /* Messages + Bottom Input */
          <>
            <div className="messages-area" ref={messagesAreaRef}>
              <div className="messages-list">
                {messages.map((msg, i) => (
                  <Message 
                    key={`${msg.role}-${i}`} 
                    role={msg.role} 
                    content={msg.content} 
                  />
                ))}
                
                {streamingMessage && (
                  <Message 
                    role="assistant" 
                    content={streamingMessage} 
                    isStreaming={true}
                  />
                )}
                
                {isLoading && !streamingMessage && (
                  <div className="message-container assistant">
                    <div className="message assistant-message">
                      <div className="typing-indicator">
                        AI is thinking
                        <div className="typing-dots">
                          <span></span>
                          <span></span>
                          <span></span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="bottom-input-area">
              <ModernInputBox
                input={input}
                setInput={setInput}
                onSubmit={sendMessage}
                isLoading={isLoading}
                disabled={isLoading || messageCount >= maxMessages}
                selectedModel={selectedModel}
                onModelChange={setSelectedModel}
                availableModels={availableModels}
              />
            </div>
          </>
        )}
      </div>


    </div>
  );
};

export default function ChatPage() {
  const { auth } = useAuth();
  return <ChatInterface auth={auth} />;
}